import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy, TPageResponse } from 'redux/types';
import { TMassPostSaleJob } from 'redux/reducers/massPostSaleJobs/types';
import { getMassPostSaleJobsRequest } from 'redux/reducers/massPostSaleJobs/reducer';
import {
	getMassPostSaleJobs,
	getMassPostSaleJobsLoading,
} from 'redux/reducers/massPostSaleJobs/selectors';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { ENavigateNames, PATHS } from 'routes/constants';
import { tableStructure } from './tableStructure';
import { EMassPostSaleJobOrderProcessingType, MASS_POST_SALE_JOBS_ACTIONS } from './constants';

export const MassPostSaleJobs: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();

	const jobs = useSelector(getMassPostSaleJobs);
	const jobsLoading = useSelector(getMassPostSaleJobsLoading);

	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TMassPostSaleJob>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	// The orders page is scoped by both the job and its processing type, but the table only hands
	// over the row id — so the processing type is read back from the clicked job.
	const handleLink = useCallback(
		(id: string) => {
			const job = jobs?.data?.find((item) => String(item.id) === id);
			const jobId = job?.jobId;
			const processingType = job?.processingType;
			if (!jobId || !processingType) return;
			dispatch(saveUrl(window.location.pathname + window.location.search));
			navigate(
				generatePath(PATHS.MASS_POST_SALE_JOB_ORDERS_PAGE, {
					jobId: String(job?.jobId ?? 0),
					processingType: job?.processingType ?? EMassPostSaleJobOrderProcessingType.WITHOUT_LC,
				}),
			);
		},
		[dispatch, navigate, jobs],
	);

	useEffect(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(tableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: tableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'jobCreated', direction: ESortDirection.DESC });

		dispatch(
			getMassPostSaleJobsRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [dispatch, search]);

	useEffect(() => {
		dispatch(getEMerchantRequest());
	}, [dispatch]);

	// const handleAction = useCallback(
	// 	(id: string, type: string) => {
	// 		if (type === MASS_POST_SALE_JOBS_ACTIONS.VIEW_ORDERS) handleLink(String(id));
	// 	},
	// 	[handleLink],
	// );

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TMassPostSaleJob>
				tableStructure={currentTableStructure}
				items={jobs as unknown as TPageResponse<TMassPostSaleJob & { id: string }>}
				itemsIsLoading={jobsLoading}
				handleAction={() => {}}
				handleLink={(id) => handleLink(String(id))}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						{ENavigateNames.MASS_POST_SALE_JOBS}
					</Typography>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
